import type {
  MemberCapacity,
  TeamCapacitySummary,
  TeamDailyCapacity,
} from '@/domains/calendar/hooks/useTeamCapacities';
import { memberJobKey } from '@/domains/calendar/lib/memberJobs';
import type { Job } from '@/domains/jobs/components/JobCard';
import { addDays, format, parseISO } from 'date-fns';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { ResourceUtilizationBar } from '../ResourceUtilizationBar';
import { GanttJobBar } from './GanttJobBar';
import { GanttMemberSubRow } from './GanttMemberSubRow';
import { useGanttScrollRef } from './GanttScrollContext';
import { layoutGanttJobBars } from './layoutJobBars';

// Auto-scroll the chart when a drag cursor approaches a horizontal edge.
const EDGE_THRESHOLD_PX = 80;
const MAX_SCROLL_SPEED_PX = 24;

interface GanttResourceRowProps {
  resourceId: string;
  resourceName: string;
  jobs: Job[]; // Jobs assigned to this resource
  rangeStart: Date; // Start date of visible range
  pixelsPerDay: number;
  totalDays: number;
  onDrop: (job: Job, resourceId: string, date: Date) => void;
  onJobClick?: (jobId: string) => void;
  dailyCapacity?: Map<string, TeamDailyCapacity>;
  summary?: TeamCapacitySummary;
  conflicts?: Map<string, Job[][]>; // Scheduling conflicts
  zoomLevel?: 'hour' | 'day' | 'week' | 'month';
  members?: MemberCapacity[];
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  memberJobs?: Map<string, Job[]>;
}

export function GanttResourceRow({
  resourceId,
  resourceName,
  jobs,
  rangeStart,
  pixelsPerDay,
  totalDays,
  onDrop,
  onJobClick,
  dailyCapacity,
  summary,
  conflicts,
  zoomLevel = 'week',
  members,
  isExpanded = false,
  onToggleExpand,
  memberJobs,
}: GanttResourceRowProps) {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [hoverDayIndex, setHoverDayIndex] = useState<number | null>(null);
  const [hoverJob, setHoverJob] = useState<Job | null>(null);

  // Edge-detect auto-scroll while dragging. Speed scales with how close the
  // cursor is to the edge so it feels smooth instead of jerky.
  const scrollContainerRef = useGanttScrollRef();
  const autoScrollFrameRef = useRef<number | null>(null);
  const autoScrollVelocityRef = useRef(0);

  const stopAutoScroll = () => {
    if (autoScrollFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
    autoScrollVelocityRef.current = 0;
  };

  const runAutoScroll = () => {
    const el = scrollContainerRef?.current;
    if (!el || autoScrollVelocityRef.current === 0) {
      stopAutoScroll();
      return;
    }
    el.scrollLeft += autoScrollVelocityRef.current;
    autoScrollFrameRef.current = requestAnimationFrame(runAutoScroll);
  };

  const updateAutoScroll = (clientX: number) => {
    const el = scrollContainerRef?.current;
    if (!el) {
      stopAutoScroll();
      return;
    }
    const bounds = el.getBoundingClientRect();
    const fromLeft = clientX - bounds.left;
    const fromRight = bounds.right - clientX;

    let velocity = 0;
    if (fromLeft < EDGE_THRESHOLD_PX && fromLeft >= 0) {
      const intensity = 1 - fromLeft / EDGE_THRESHOLD_PX;
      velocity = -Math.ceil(intensity * MAX_SCROLL_SPEED_PX);
    } else if (fromRight < EDGE_THRESHOLD_PX && fromRight >= 0) {
      const intensity = 1 - fromRight / EDGE_THRESHOLD_PX;
      velocity = Math.ceil(intensity * MAX_SCROLL_SPEED_PX);
    }

    autoScrollVelocityRef.current = velocity;
    if (velocity !== 0 && autoScrollFrameRef.current === null) {
      autoScrollFrameRef.current = requestAnimationFrame(runAutoScroll);
    } else if (velocity === 0) {
      stopAutoScroll();
    }
  };

  useEffect(() => stopAutoScroll, []);

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'JOB',
    canDrop: (item: Job, monitor) => {
      const offset = monitor.getClientOffset();
      if (!offset || !timelineRef.current) return false;

      const timelineBounds = timelineRef.current.getBoundingClientRect();
      const relativeX = offset.x - timelineBounds.left;
      const dayIndex = Math.floor(relativeX / pixelsPerDay);
      const clampedDayIndex = Math.max(0, Math.min(dayIndex, totalDays - 1));

      const dropDate = new Date(rangeStart);
      dropDate.setDate(dropDate.getDate() + clampedDayIndex);

      // Don't allow drops on past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dropDate >= today;
    },
    drop: (item: Job, monitor) => {
      const offset = monitor.getClientOffset();
      if (!offset || !timelineRef.current) return;

      // Get timeline element bounds
      const timelineBounds = timelineRef.current.getBoundingClientRect();

      // Calculate X position relative to timeline start (after the 200px resource column)
      const relativeX = offset.x - timelineBounds.left;

      // Calculate which day was dropped on
      const dayIndex = Math.floor(relativeX / pixelsPerDay);
      const clampedDayIndex = Math.max(0, Math.min(dayIndex, totalDays - 1));

      // Calculate the actual date
      const dropDate = new Date(rangeStart);
      dropDate.setDate(dropDate.getDate() + clampedDayIndex);

      onDrop(item, resourceId, dropDate);

      // Clear hover + scroll state
      stopAutoScroll();
      setHoverDayIndex(null);
      setHoverJob(null);
    },
    hover: (item: Job, monitor) => {
      const offset = monitor.getClientOffset();
      if (!offset || !timelineRef.current) {
        setHoverDayIndex(null);
        setHoverJob(null);
        stopAutoScroll();
        return;
      }

      updateAutoScroll(offset.x);

      // Get timeline element bounds
      const timelineBounds = timelineRef.current.getBoundingClientRect();

      // Calculate X position relative to timeline start
      const relativeX = offset.x - timelineBounds.left;

      // Calculate which day is being hovered
      const dayIndex = Math.floor(relativeX / pixelsPerDay);
      const clampedDayIndex = Math.max(0, Math.min(dayIndex, totalDays - 1));

      setHoverDayIndex(clampedDayIndex);
      setHoverJob(item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const { bars: jobBars, laneCount } = layoutGanttJobBars(jobs, {
    rangeStart,
    pixelsPerDay,
    totalDays,
    zoomLevel,
  });
  const rowHeight = Math.max(60, laneCount * 56 + 8); // 56px per lane + 8px padding

  // Hour-zoom utilization: bucket jobs into per-hour booked hours so the
  // capacity bars align with the hourly columns instead of stretching across
  // the whole day.
  const hourlyUtilization = useMemo(() => {
    if (zoomLevel !== 'hour') return null;
    const totalHours = totalDays * 24;
    const booked = new Array<number>(totalHours).fill(0);
    for (const job of jobs) {
      if (!job.startTime || !job.endTime) continue;
      const start = parseISO(job.startTime).getTime();
      const end = parseISO(job.endTime).getTime();
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
      // Distribute the job's hours into hourly buckets.
      const rangeStartMs = rangeStart.getTime();
      let cursor = Math.max(start, rangeStartMs);
      const stop = Math.min(end, rangeStartMs + totalHours * 60 * 60 * 1000);
      while (cursor < stop) {
        const idx = Math.floor((cursor - rangeStartMs) / (60 * 60 * 1000));
        const hourEnd = rangeStartMs + (idx + 1) * 60 * 60 * 1000;
        const chunkEnd = Math.min(stop, hourEnd);
        booked[idx] += (chunkEnd - cursor) / (60 * 60 * 1000);
        cursor = chunkEnd;
      }
    }
    // Available hours per hour bucket: use team summary's average available/day
    // divided by 24 (BE doesn't expose hour-grain availability today).
    const availablePerHour = summary
      ? summary.totalAvailableHours / Math.max(totalDays * 24, 1)
      : 1;
    return booked.map((b) => ({
      bookedHours: b,
      availableHours: availablePerHour,
      utilizationPercent: availablePerHour > 0 ? (b / availablePerHour) * 100 : 0,
    }));
  }, [zoomLevel, jobs, totalDays, rangeStart, summary]);

  const canExpand = !!onToggleExpand && (members?.length ?? 0) > 0;

  return (
    <>
      <div
        ref={drop}
        // `isolate` (isolation: isolate) keeps this row's z-indexed descendants
        // — the sticky team cell and the over-allocation icon — inside their own
        // stacking context. Without it they resolve against the scroll container
        // and paint over the sticky header (WRK-5717).
        className={`flex border-b border-border transition-colors isolate ${
          isOver && canDrop ? 'bg-blue-50' : isOver ? 'bg-red-50' : 'hover:bg-muted/20'
        }`}
        style={{ minHeight: `${rowHeight}px` }}
        onMouseLeave={() => {
          // Clear hover + auto-scroll state when mouse leaves row
          setHoverDayIndex(null);
          setHoverJob(null);
          stopAutoScroll();
        }}
      >
        {/* Team name (sticky left column) */}
        <div className="w-[200px] flex-shrink-0 border-r-2 border-border px-4 py-3 bg-card flex items-center sticky left-0 z-30">
          <div className="w-full space-y-1.5">
            <div className="flex items-center gap-1.5">
              {canExpand ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  className="flex-shrink-0 -ml-1 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={
                    isExpanded
                      ? `Collapse ${resourceName} members`
                      : `Expand ${resourceName} members`
                  }
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              ) : (
                <span className="w-4 flex-shrink-0" aria-hidden="true" />
              )}
              <div className="font-semibold text-sm truncate">{resourceName}</div>
            </div>
            <div className="text-xs text-muted-foreground pl-[18px]">{jobs.length} jobs</div>
            {summary && (
              <div className="pl-[18px]">
                <ResourceUtilizationBar
                  utilizationPercent={summary.averageUtilization}
                  bookedHours={summary.totalBookedHours}
                  availableHours={summary.totalAvailableHours}
                />
              </div>
            )}
          </div>
        </div>

        {/* Timeline area with job bars */}
        <div
          ref={timelineRef}
          className="flex-1 relative overflow-hidden"
          style={{
            width: `${totalDays * pixelsPerDay}px`,
            minHeight: `${rowHeight}px`,
          }}
        >
          {/* Empty state hint for rows with no jobs */}
          {jobs.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-xs text-muted-foreground">No jobs scheduled</span>
            </div>
          )}

          {/* Hour-zoom: per-hour capacity buckets across the visible range */}
          {zoomLevel === 'hour' &&
            hourlyUtilization &&
            (() => {
              const pixelsPerHour = pixelsPerDay / 24;
              return hourlyUtilization.map((bucket, hourIdx) => {
                const pct = bucket.utilizationPercent;
                let color = 'bg-green-500/70';
                if (pct > 100) color = 'bg-red-500/70';
                else if (pct >= 90) color = 'bg-yellow-500/70';
                if (pct <= 0) return null;
                return (
                  <div
                    key={`hour-${hourIdx}`}
                    className={`absolute bottom-0 ${color} transition-all`}
                    style={{
                      left: `${hourIdx * pixelsPerHour}px`,
                      width: `${pixelsPerHour}px`,
                      height: `${Math.min(pct, 100)}%`,
                      maxHeight: '100%',
                    }}
                    title={`${bucket.bookedHours.toFixed(1)}h booked (${pct.toFixed(0)}%)`}
                  />
                );
              });
            })()}

          {/* Day grid lines and capacity visualization */}
          {Array.from({ length: totalDays }).map((_, i) => {
            const date = new Date(rangeStart);
            date.setDate(date.getDate() + i);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            const dateStr = format(date, 'yyyy-MM-dd');

            // Check if date is in the past
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isPast = date < today;

            // Get capacity for this day
            const capacity = dailyCapacity?.get(dateStr);
            const bookedHours = capacity?.totalHours ?? 0;
            const utilizationPercent = capacity?.utilizationPercent || 0;
            // Only treat the day as over-allocated if there are actually
            // booked hours — otherwise the BE rounding of a 0h day can still
            // hint at red, which confuses operators on empty days.
            const isOverallocated = (capacity?.isOverallocated ?? false) && bookedHours > 0;
            const hasConflicts = conflicts?.has(dateStr) || false;
            const hasBooked = bookedHours > 0;

            // Capacity bar color based on utilization (day-level cells; the
            // hour-zoom branch above renders per-hour bars instead).
            let capacityColor = 'bg-green-500/70';
            let bgHighlight = '';
            if (hasBooked && (utilizationPercent > 100 || isOverallocated)) {
              capacityColor = 'bg-red-500/70';
              bgHighlight = 'bg-red-50/30 dark:bg-red-950/20';
            } else if (hasBooked && utilizationPercent >= 90) {
              capacityColor = 'bg-yellow-500/70';
              bgHighlight = 'bg-yellow-50/20 dark:bg-yellow-950/10';
            }

            return (
              <div
                key={i}
                className={`absolute top-0 bottom-0 border-r border-border/30 ${
                  isPast
                    ? 'bg-muted/40 opacity-60'
                    : isWeekend
                      ? 'bg-slate-100/60 dark:bg-slate-800/40'
                      : bgHighlight
                }`}
                style={{
                  left: `${i * pixelsPerDay}px`,
                  width: `${pixelsPerDay}px`,
                }}
              >
                {/* Overallocation warning icon - only show when actually over capacity */}
                {isOverallocated && (
                  <div className="absolute top-1 right-1 z-40">
                    <AlertTriangle
                      className="w-4 h-4 text-red-600 dark:text-red-400"
                      title="Over-allocated - exceeds team capacity"
                    />
                  </div>
                )}

                {/* Capacity bar (bottom of cell) — only in day/week/month zoom;
                  hour zoom uses the per-hour bars rendered above. Hide when
                  no work is booked so empty (incl. weekend) days stay clean. */}
                {zoomLevel !== 'hour' && capacity && hasBooked && (
                  <div
                    className={`absolute bottom-0 left-0 right-0 ${capacityColor} transition-all`}
                    style={{
                      height: `${Math.min(utilizationPercent, 100)}%`,
                      maxHeight: '100%',
                    }}
                    title={`${utilizationPercent.toFixed(1)}% utilised (${bookedHours.toFixed(1)}h / ${capacity.availableHours}h)${
                      capacity.membersOnLeave.length > 0
                        ? ` — ${capacity.membersOnLeave.length} on leave`
                        : ''
                    }${isOverallocated ? ' — OVER CAPACITY' : ''}`}
                  />
                )}
              </div>
            );
          })}

          {/* Job bars with vertical stacking */}
          {jobBars.map((bar, index) => {
            const topPosition = 4 + bar.lane * 56; // 4px top padding + 56px per lane
            return (
              <div
                key={`${bar.job.id}-${bar.lane}-${index}`}
                className="absolute"
                style={{
                  left: `${bar.left}px`,
                  top: `${topPosition}px`,
                  width: `${bar.width}px`,
                }}
              >
                <GanttJobBar job={bar.job} left={0} width={bar.width} onJobClick={onJobClick} />
              </div>
            );
          })}

          {/* Drop indicator - shows where job will land */}
          {isOver && hoverDayIndex !== null && hoverJob && (
            <div
              className={`absolute top-1 h-12 border-2 border-dashed pointer-events-none rounded ${
                canDrop ? 'border-blue-500 bg-blue-100/30' : 'border-red-500 bg-red-100/30'
              }`}
              style={{
                left: `${hoverDayIndex * pixelsPerDay}px`,
                width: `${Math.max(
                  (hoverJob.duration ?? (hoverJob.estimatedHours * 60 || 60)) *
                    (pixelsPerDay / 480),
                  pixelsPerDay,
                )}px`,
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded ${
                    canDrop ? 'text-blue-700 bg-white/80' : 'text-red-700 bg-white/80'
                  }`}
                >
                  {canDrop
                    ? format(addDays(rangeStart, hoverDayIndex), 'MMM d')
                    : 'Cannot schedule in past'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {isExpanded &&
        members?.map((member) => (
          <GanttMemberSubRow
            key={member.userId}
            member={member}
            rangeStart={rangeStart}
            pixelsPerDay={pixelsPerDay}
            totalDays={totalDays}
            jobs={memberJobs?.get(memberJobKey(member.teamId, member.userId))}
            zoomLevel={zoomLevel}
            onJobClick={onJobClick}
          />
        ))}
    </>
  );
}
